import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const getDateRange = (range: string) => {
  const today = new Date();
  let start = new Date();
  let end = new Date();

  switch (range) {
    case "month":
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
      break;
    case "30days":
      start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "90days":
      start = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case "all":
      start = new Date(2000, 0, 1);
      end = new Date(2099, 11, 31);
      break;
    case "year":
    default:
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
      break;
  }

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
    start,
    end,
  };
};
import { MetricCard } from "@/components/dashboard/MetricCard";
import { RevenueChart } from "@/components/dashboard/RevenueChart";
import { OpportunityPieChart } from "@/components/dashboard/OpportunityPieChart";
import { SalesFunnelBarChart } from "@/components/dashboard/SalesFunnelBarChart";
import {
  useGetDashboardMetrics,
  useGetSalesFunnel,
  useGetContactsReport,
  useGetDashboardOpportunities,
} from "@/hooks/queries/useDashboardMetrics";
import { useAuth } from "@/hooks/useAuth";
import { companyAPI, Company } from "@/services/companyService";
import {
  DollarSign,
  Target,
  Building2,
  Users,
  TrendingUp,
  Activity,
  Calendar,
  Loader2,
  AlertCircle,
  LayoutDashboard,
} from "lucide-react";

export default function Dashboard() {
  const { company } = useAuth();
  const slug = company?.slug;
  const [dateRange, setDateRange] = useState<string>("year");

  // Custom date picker states (defaulting to current month range)
  const defaultDates = useMemo(() => {
    const today = new Date();
    const firstDayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
    const lastDayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
    return { firstDayStr, lastDayStr };
  }, []);

  const [customStartDate, setCustomStartDate] = useState<string>(defaultDates.firstDayStr);
  const [customEndDate, setCustomEndDate] = useState<string>(defaultDates.lastDayStr);

  const { startDate, endDate, start, end } = useMemo(() => {
    if (dateRange === "custom") {
      const s = new Date(customStartDate + "T00:00:00");
      const e = new Date(customEndDate + "T23:59:59");
      return {
        startDate: customStartDate,
        endDate: customEndDate,
        start: s,
        end: e,
      };
    }
    return getDateRange(dateRange);
  }, [dateRange, customStartDate, customEndDate]);

  const { data: metrics, isLoading: loadingMetrics, error: metricsError } = useGetDashboardMetrics(slug);
  const { data: funnelResponse, isLoading: loadingFunnel } = useGetSalesFunnel(slug, startDate, endDate);
  const { data: contactsReportResponse, isLoading: loadingContacts } = useGetContactsReport(slug, startDate, endDate);
  const { data: allOpportunities = [], isLoading: loadingOpportunities } = useGetDashboardOpportunities(slug);

  const { data: companiesResponse, isLoading: loadingCompanies } = useQuery({
    queryKey: ["companies", slug],
    queryFn: async () => {
      const response = await companyAPI.getCompanies({ slug: slug as string, limit: 100 });
      return response.data;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });

  const loading = loadingMetrics || loadingFunnel || loadingContacts || loadingCompanies || loadingOpportunities;
  const error = metricsError ? "Error loading metrics" : null;
  const funnelData = funnelResponse?.stages || [];
  const contactsReport = contactsReportResponse?.report || [];
  const companies = companiesResponse || [];

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) {
      return (company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };
  const currencySymbol = getCurrencySymbol();

  // Mostrar advertencia si hay error pero data disponible
  const hasError = error !== null;



  // Filter opportunities on the client side according to the selected date range
  const filteredOpportunities = useMemo(() => {
    if (!allOpportunities) return [];
    return allOpportunities.filter((opp) => {
      const createdDate = new Date(opp.created_at);
      return createdDate >= start && createdDate <= end;
    });
  }, [allOpportunities, start, end]);

  const wonOpportunities = useMemo(() => {
    return filteredOpportunities.filter(
      (opp) => opp.stage?.stage_type === "closed_won"
    );
  }, [filteredOpportunities]);

  const totalWonValue = useMemo(() => {
    return wonOpportunities.reduce(
      (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
      0
    );
  }, [wonOpportunities]);

  const totalValue = useMemo(() => {
    return filteredOpportunities.reduce(
      (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
      0
    );
  }, [filteredOpportunities]);

  const conversionRate = useMemo(() => {
    return filteredOpportunities.length > 0
      ? (wonOpportunities.length / filteredOpportunities.length) * 100
      : 0;
  }, [filteredOpportunities, wonOpportunities]);

  const topCompanies = useMemo(() => {
    return companies
      .map((comp) => {
        const companyOpportunities = filteredOpportunities.filter(
          (opp) =>
            opp.client_company?.id === comp.id &&
            opp.stage?.stage_type !== "closed_won" &&
            opp.stage?.stage_type !== "closed_lost"
        );

        const companyTotalValue = companyOpportunities.reduce(
          (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
          0
        );

        return {
          ...comp,
          opportunities: companyOpportunities,
          totalValue: companyTotalValue,
        };
      })
      .filter((comp) => comp.opportunities.length > 0)
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 5);
  }, [companies, filteredOpportunities]);

  const avgDealSize = useMemo(() => {
    return filteredOpportunities.length > 0
      ? totalValue / filteredOpportunities.length
      : 0;
  }, [filteredOpportunities, totalValue]);

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlyActivity = useMemo(() => {
    return filteredOpportunities.filter((opp) => {
      const oppDate = new Date(opp.estimated_close_date || opp.created_at);
      return (
        oppDate.getMonth() === currentMonth &&
        oppDate.getFullYear() === currentYear
      );
    }).length;
  }, [filteredOpportunities, currentMonth, currentYear]);

  const totalOpportunitiesInFunnel = funnelData.reduce(
    (sum, stage) => sum + stage.opportunities_count,
    0
  );

  const calculateAvgClosingTime = () => {
    if (wonOpportunities.length === 0) {
      return { days: 0, label: "N/A" };
    }

    const totalDays = wonOpportunities.reduce((sum, opp) => {
      const createdDate = new Date(opp.created_at);
      const closedDate = new Date(opp.stage_entered_at || opp.updated_at);
      const daysDiff = Math.floor(
        (closedDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      return sum + daysDiff;
    }, 0);

    const avgDays = Math.round(totalDays / wonOpportunities.length);

    return {
      days: avgDays,
      label: `${avgDays} días`,
    };
  };

  const avgClosingTime = calculateAvgClosingTime();

  const monthlyRevenueData = (() => {
    const revenueMap: Record<string, number> = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const monthStr = d.toLocaleString("es-ES", { month: "short" });
      revenueMap[monthStr] = 0;
    }
    wonOpportunities.forEach((opp) => {
      const d = new Date(opp.stage_entered_at || opp.updated_at);
      const monthStr = d.toLocaleString("es-ES", { month: "short" });
      if (revenueMap[monthStr] !== undefined) {
        revenueMap[monthStr] += parseFloat(opp.estimated_value || "0");
      }
    });
    return Object.keys(revenueMap).map((key) => ({
      month: key.charAt(0).toUpperCase() + key.slice(1),
      revenue: revenueMap[key],
    }));
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-slate-600">Cargando métricas...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <div className="text-lg text-red-600">
            Error al cargar las métricas
          </div>
          <p className="text-slate-500 text-sm">
            No se pudieron obtener los datos del servidor
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col p-4 sm:p-8 bg-white sm:rounded-[32px] sm:shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] sm:border border-slate-200/60 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-indigo-100 p-2.5 rounded-2xl">
              <LayoutDashboard className="h-7 w-7 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">Resumen general de tu CRM</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">Período:</span>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200 text-slate-700 font-medium rounded-xl h-10">
                <SelectValue placeholder="Seleccionar período" />
              </SelectTrigger>
              <SelectContent className="bg-white rounded-xl border border-slate-200">
                <SelectItem value="month">Este Mes</SelectItem>
                <SelectItem value="30days">Últimos 30 días</SelectItem>
                <SelectItem value="90days">Últimos 90 días</SelectItem>
                <SelectItem value="year">Este Año</SelectItem>
                <SelectItem value="all">Todo</SelectItem>
                <SelectItem value="custom">Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dateRange === "custom" && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-200">
              <span className="text-sm font-medium text-slate-400">Desde:</span>
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-[150px] bg-slate-50 border-slate-200 text-slate-700 rounded-xl h-10 text-sm"
              />
              <span className="text-sm font-medium text-slate-400">Hasta:</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-[150px] bg-slate-50 border-slate-200 text-slate-700 rounded-xl h-10 text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 border-0 shadow-[0_8px_30px_rgb(59,130,246,0.3)] hover:shadow-[0_8px_30px_rgb(59,130,246,0.5)] hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
          {/* Subtle decorative circle */}
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-white/10 blur-2xl"></div>
          <CardContent className="p-6 relative z-10">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-xs font-bold text-blue-100/90 tracking-wider uppercase mb-1">
                  Ingresos Ganados
                </p>
                <p className="text-3xl font-bold text-white tracking-tight">{`${currencySymbol}${totalWonValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 text-white font-bold text-xl flex-shrink-0 backdrop-blur-md border border-white/30 shadow-inner">
                {currencySymbol}
              </div>
            </div>
          </CardContent>
        </Card>

        <MetricCard
          title="Oportunidades"
          value={filteredOpportunities.length}
          icon={Target}
        />

        <MetricCard
          title="Empresas"
          value={metrics.totalCompanies}
          icon={Building2}
        />

        <MetricCard
          title="Contactos"
          value={metrics.totalContacts}
          icon={Users}
          // trend={{
          //   value: 8.3,
          //   isPositive: true,
          // }}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueChart data={monthlyRevenueData} />
        <OpportunityPieChart data={funnelData} />
      </div>

      {/* Additional Metrics */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Conversión de Leads"
          value={`${conversionRate.toFixed(2)}%`}
          icon={TrendingUp}
        />

        <MetricCard
          title="Tiempo Promedio de Cierre"
          value={avgClosingTime.label}
          icon={Calendar}
        />

        <MetricCard
          title="Actividad Mensual"
          value={monthlyActivity}
          icon={Activity}
          // trend={{
          //   value: 12.5,
          //   isPositive: true
          // }}
        />

        <Card className="bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-500 mb-2 tracking-wide">Valor Promedio Deal</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight truncate">{`${currencySymbol}${(avgDealSize / 1000).toFixed(1)}K`}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-white/60 shadow-inner flex-shrink-0">
                <span className="font-bold text-slate-700 text-lg">{currencySymbol}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Detailed Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <Card className="bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Top Empresas por Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topCompanies.length > 0 ? (
                topCompanies.map((comp, index) => (
                  <div
                    key={comp.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white text-sm font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {comp.name}
                        </p>
                        <p className="text-sm text-slate-600">
                          {comp.industry?.name || "Sin industria"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {currencySymbol}
                        {(comp.totalValue / 1000).toFixed(1)}K
                      </p>
                      <p className="text-sm text-slate-600">
                        {comp.opportunities.length} oportunidades
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">
                  No hay empresas con oportunidades activas
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur-xl border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Rendimiento por Etapa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SalesFunnelBarChart data={funnelData} currencySymbol={currencySymbol} />
          </CardContent>
        </Card>
      </div>


    </div>
  );
}
